import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity, AlertTriangle, ArrowRight, Bed, Clock, CreditCard, Filter,
  HeartPulse, Hospital, Search, ShieldAlert, UserPlus, Users
} from 'lucide-react'
import KPICard from '../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import { patientService } from '../../../services/patientApi'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { ageFromBirth, formatDateTime, genderLabel } from './patientFormat'
import NewPatientModal from './NewPatientModal'
import AddVitalSignsModal from './AddVitalSignsModal'
import PatientAddressMenu from './PatientAddressMenu'
import { PatientsCardsSkeleton, PatientsKpiSkeleton } from './PatientsSkeletons'
import PatientShiftStack from './PatientShiftStack'
import '../Financeiro/Dashboard/FinanceDashboardPage.css'
import './PatientsPage.css'

const STATUS = {
  internado: { label: 'Internado', badge: 'patients-badge patients-badge--internado' },
  estavel: { label: 'Estável', badge: 'patients-badge patients-badge--estavel' },
  observacao: { label: 'Em observação', badge: 'patients-badge patients-badge--observacao' },
  ambulatorial: { label: 'Ambulatorial', badge: 'patients-badge patients-badge--ambulatorial' },
  alta: { label: 'Alta concedida', badge: 'patients-badge patients-badge--alta' }
}

function initials(name) {
  return String(name || '?').split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
}

function PatientsListPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canCreate = hasPermission(MODULES.PATIENTS, ACTIONS.CREATE)
  const canRegisterVitals = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_VITALS_RECORD)
  const [patients, setPatients] = useState([])
  const [summary, setSummary] = useState({})
  const [insurers, setInsurers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedInsurance, setSelectedInsurance] = useState('all')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false)
  const [vitalModalPatient, setVitalModalPatient] = useState(null)
  const requestSeq = useRef(0)
  const hasRequested = useRef(false)

  const loadPatients = useCallback(async () => {
    const seq = ++requestSeq.current
    hasRequested.current = true
    setLoading(true)
    setError('')
    try {
      const response = await patientService.list({
        search: searchTerm,
        status: selectedStatus,
        insurance: selectedInsurance
      })
      if (seq !== requestSeq.current) return
      setPatients(response.patients || [])
      setSummary(response.summary || {})
      setInsurers(response.insurers || [])
    } catch (err) {
      if (seq !== requestSeq.current) return
      setError(err.response?.data?.message || 'Não foi possível carregar os pacientes')
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false)
        setHasLoaded(true)
      }
    }
  }, [searchTerm, selectedStatus, selectedInsurance])

  useEffect(() => {
    const delay = hasRequested.current ? 250 : 0
    const timer = setTimeout(loadPatients, delay)
    return () => clearTimeout(timer)
  }, [loadPatients])

  useEffect(() => {
    const refresh = () => loadPatients()
    window.addEventListener('wfmed-shift-updated', refresh)
    return () => window.removeEventListener('wfmed-shift-updated', refresh)
  }, [loadPatients])

  const showSkeleton = loading && (!hasLoaded || patients.length === 0) && !error
  const showRefreshing = loading && hasLoaded && patients.length > 0

  const kpis = [
    { label: 'Internados em leito', value: String(summary.internado || 0), change: `${summary.total || 0} cadastrados`, up: true, icon: Bed, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'Em observação', value: String(summary.observacao || 0), change: 'aguardando', up: false, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50' },
    { label: 'Ambulatorial', value: String(summary.ambulatorial || 0), change: 'retorno', up: true, icon: Hospital, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Alertas de alergia', value: String(summary.with_allergy || 0), change: 'atenção', up: false, icon: AlertTriangle, color: 'text-rose-500', bg: 'bg-rose-50' }
  ]

  return (
    <div className="finance-dashboard patients-module" aria-busy={loading}>
      <div className="finance-dashboard-header">
        <div>
          <h1 className="finance-dashboard-title">
            Gestão de <span className="finance-dashboard-title-highlight">Pacientes</span>
          </h1>
          <p className="finance-dashboard-subtitle">
            Prontuário eletrônico, leitos, sinais vitais e faturamento hospitalar
          </p>
        </div>
        {canCreate && (
          <div className="finance-dashboard-header-actions">
              <button type="button" onClick={() => setIsNewPatientModalOpen(true)} className="finance-dashboard-new-btn">
                <UserPlus size={16} /> Admitir paciente
              </button>
          </div>
        )}
      </div>

      {loading && !showRefreshing && <p className="patients-skel-sr">Carregando pacientes</p>}
      {showRefreshing && <div className="patients-refresh-bar" aria-hidden="true" />}

      {error && (
        <div className="finance-dashboard-error-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={loadPatients}>Tentar novamente</button>
        </div>
      )}

      {showSkeleton ? (
        <PatientsKpiSkeleton />
      ) : (
        <div className={`finance-dashboard-kpi-grid patients-reveal${showRefreshing ? ' is-refreshing' : ''}`}>
          {kpis.map((kpi) => (
            <KPICard key={kpi.label} {...kpi} />
          ))}
        </div>
      )}

      <div className="patients-toolbar">
        <div className="patients-toolbar-row">
          <div className="patients-search">
            <Search size={16} />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome, prontuário, CPF, leito ou convênio..."
            />
          </div>
          <div className="patients-select-wrap">
            <Filter size={14} />
            <select value={selectedInsurance} onChange={(e) => setSelectedInsurance(e.target.value)} className="patients-select">
              <option value="all">Todos os convênios</option>
              {insurers.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
        </div>
        <div className="patients-chips">
          {[
            ['all', 'Todos os pacientes'],
            ['internado', 'Internados'],
            ['estavel', 'Estável'],
            ['observacao', 'Em observação'],
            ['ambulatorial', 'Ambulatorial'],
            ['alta', 'Alta concedida'],
            ['arquivado', 'Arquivados']
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setSelectedStatus(id)}
              className={`patients-chip${selectedStatus === id ? ' is-active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {showSkeleton ? (
        <PatientsCardsSkeleton />
      ) : (
      <div className={`patients-results patients-reveal${showRefreshing ? ' is-refreshing' : ''}`}>
      <div className="patients-grid patients-grid--cards">
        {patients.map((patient) => {
          const allergies = Array.isArray(patient.allergies) ? patient.allergies : []
          const alerts = Array.isArray(patient.alerts) ? patient.alerts : []
          const badge = STATUS[patient.status] || STATUS.internado
          const age = ageFromBirth(patient.birth_date)
          return (
            <article key={patient.id} className="patients-card">
              <div>
                <div className="patients-card-top">
                  <div className="patients-identity">
                    <div className="patients-avatar-wrap">
                      {patient.photo_url
                        ? <img src={patient.photo_url} alt="" className="patients-avatar" />
                        : <div className="patients-avatar">{initials(patient.name)}</div>}
                      {patient.blood_type && <span className="patients-blood">{patient.blood_type}</span>}
                    </div>
                    <div>
                      <span className={patient.archived_at ? 'patients-badge patients-badge--alta' : badge.badge}>
                        {patient.archived_at ? (patient.archive_reason === 'obito' ? 'Arquivado · Óbito' : 'Arquivado · Saída') : badge.label}
                      </span>
                      <h3 className="patients-name">{patient.name}</h3>
                      <p className="patients-muted">
                        {age != null ? `${age} anos` : 'Idade não informada'} · {genderLabel(patient.gender)}
                      </p>
                    </div>
                  </div>
                  <PatientShiftStack people={patient.on_shift} />
                </div>
                <div className="patients-meta patients-meta--card">
                  <span className="patients-inline"><Bed size={14} className="tone-green" /> {patient.bed || 'Sem leito fixo'}</span>
                  <span className="patients-inline"><CreditCard size={14} className="tone-green" /> {patient.insurance_provider || 'Particular'}</span>
                  <PatientAddressMenu patient={patient} />
                </div>
                {(allergies.length > 0 || alerts.length > 0) && (
                  <div className="patients-stack" style={{ marginTop: '0.75rem', gap: '0.4rem' }}>
                    {allergies.length > 0 && (
                      <div className="patients-tags">
                        <span className="patients-tag patients-tag--rose"><AlertTriangle size={11} /> Alergia</span>
                        {allergies.slice(0, 2).map((item) => <span key={item} className="patients-tag patients-tag--rose">{item}</span>)}
                      </div>
                    )}
                    {alerts.length > 0 && (
                      <div className="patients-tags">
                        <span className="patients-tag patients-tag--amber"><ShieldAlert size={11} /> {alerts[0]}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="patients-vitals" style={{ marginTop: '0.75rem' }}>
                  <div className="patients-vitals-head">
                    <span className="patients-inline"><Activity size={12} className="tone-rose" /> Últimos sinais vitais</span>
                    <span>{patient.vital_recorded_at ? formatDateTime(patient.vital_recorded_at) : '--:--'}</span>
                  </div>
                  {patient.vital_recorded_at ? (
                    <div className="patients-vitals-grid">
                      <div><small>PA</small><strong>{patient.vital_systolic || '—'}/{patient.vital_diastolic || '—'}</strong></div>
                      <div><small>FC</small><strong className="tone-amber">{patient.vital_heart_rate || '—'}</strong></div>
                      <div><small>SpO2</small><strong className="tone-sky">{patient.vital_oxygen || '—'}%</strong></div>
                      <div><small>Temp</small><strong className="tone-rose">{patient.vital_temperature || '—'}°C</strong></div>
                    </div>
                  ) : <p className="patients-muted" style={{ textAlign: 'center' }}>Nenhum sinal vital aferido.</p>}
                </div>
              </div>
              <div className="patients-card-actions">
                {canRegisterVitals && !patient.archived_at && (
                  <button type="button" onClick={() => setVitalModalPatient(patient)} className="patients-btn patients-btn--rose">
                    <HeartPulse size={14} /> Sinais
                  </button>
                )}
                <button type="button" onClick={() => navigate(`/erp/pacientes/${patient.id}`)} className="patients-btn patients-btn--dark">
                  Abrir prontuário <ArrowRight size={14} />
                </button>
              </div>
            </article>
          )
        })}
      </div>

      {patients.length === 0 && !error && (
        <div className="patients-empty">
          <Users size={36} />
          <h3>Nenhum paciente encontrado</h3>
          <p className="patients-muted">Ajuste os filtros ou admita um novo paciente.</p>
        </div>
      )}
      </div>
      )}

      <NewPatientModal
        isOpen={isNewPatientModalOpen}
        onClose={() => setIsNewPatientModalOpen(false)}
        onCreated={(patient) => navigate(`/erp/pacientes/${patient.id}`)}
      />
      <AddVitalSignsModal
        isOpen={Boolean(vitalModalPatient)}
        patientId={vitalModalPatient?.id}
        patientName={vitalModalPatient?.name}
        onClose={() => setVitalModalPatient(null)}
        onAdded={loadPatients}
      />
    </div>
  )
}

export default PatientsListPage
